import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
  Input as HeroInput,
  Radio as HeroRadio,
  Select as HeroSelect,
  Textarea as HeroTextarea,
  RadioGroup,
  SelectItem,
} from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import {
  Controller,
  type FieldArrayWithId,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import MeetingLocationSelector from '@/app/components/account/meeting-location-selector.tsx';
import NoAccountBanner from '@/app/components/account/no-account-banner.tsx';
import EventTimeAndLocation from '@/app/components/calendar/event-time-and-location.tsx';
import RepresentativeAccess from '@/app/components/representative-access.tsx';
import StaffAvailabilityList from '@/app/components/staff-availability-list.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import toast from '@/app/toast.ts';
import type { AccountMeetingLocationModel } from '@/app/types/account.type.ts';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/primitives/dialog.tsx';
import { Fieldset, Label } from '@/primitives/fieldset.tsx';
import { Heading, Subheading } from '@/primitives/heading.tsx';
import { Switch, SwitchField } from '@/primitives/switch.tsx';
import {
  useFetchDaysOfWeekListQuery,
  usePostAvailabilitySlotMutation,
} from '@/slices/availability-slot-api-slice.ts';
import { useFetchStaffMembersQuery } from '@/slices/staff-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import type { AccountCategoryModel } from '../../../types/account-category.type.ts';
import type { AccountRepresentativeListModel } from '../../../types/account-representative.type.ts';
import {
  type AvailabilitySlotListModel,
  type AvailabilitySlotPostModel,
  RecurringFrequencyEnum,
  type RecurringOptions,
  type StaffMemberAvailabilityModel,
  WeekOfMonthEnum,
} from '../../../types/availability-slot.type.ts';
import type { ConfirmationStatusEnum } from '../../../types/confirmation-status.model.ts';
import { getDateFormat, getTimeFormat } from '../../../types/constants.ts';
import { sortStaffMembers } from './helpers-availability.ts';

const daysOfWeek = [
  { value: 'Sunday', label: 'Sun' },
  { value: 'Monday', label: 'Mon' },
  { value: 'Tuesday', label: 'Tue' },
  { value: 'Wednesday', label: 'Wed' },
  { value: 'Thursday', label: 'Thu' },
  { value: 'Friday', label: 'Fri' },
  { value: 'Saturday', label: 'Sat' },
];

export interface NewAvailabilitySlotForm {
  date: string;
  startTime: string;
  endTime: string;
  officeNotes: string;
  accountMeetingLocation: AccountMeetingLocationModel;
  accountRepresentativeIds: number[];
  accountCategoryIds: number[];
  staffMemberAvailabilities: StaffMemberAvailabilityModel[];
  recurringOptions: Omit<RecurringOptions, 'timeZone'>;
}

const NewAvailability = () => {
  const { currentAccount } = useSecurityContext();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: accountStaffMembers, isLoading } = useFetchStaffMembersQuery(
    currentAccount?.key ? `accountKey=${currentAccount?.key}` : skipToken,
  );
  const [postAvailability, { isLoading: postLoading }] = usePostAvailabilitySlotMutation();
  const { data: daysOfWeekListForMonth } = useFetchDaysOfWeekListQuery();
  const form = useForm<NewAvailabilitySlotForm>({
    defaultValues: {
      date: DateTime.now().toISODate(),
      recurringOptions: {
        frequency: RecurringFrequencyEnum.Day,
        endDate: null,
        recurEvery: 1,
        everyWeekday: false,
        daysOfWeek: [],
        dateOfMonth: 1,
        weekOfMonth: WeekOfMonthEnum.First,
        dayOfWeek: 'Day',
      },
    },
    criteriaMode: 'all',
    resetOptions: {
      keepErrors: false,
    },
  });

  const { control, watch, setValue, handleSubmit, getValues } = form;
  const [isRecurring, setIsRecurring] = useState(false);
  const [confirmRecurringOpen, setConfirmRecurringOpen] = useState(false);
  const daysOfWeekWatch = watch('recurringOptions.daysOfWeek');
  const recurringFrequencyWatch = watch('recurringOptions.frequency');
  const [monthlySelection, setMonthlySelection] = useState<'date' | 'week'>('date');
  const [conflictingAvailabilitySlot, setConflictingAvailabilitySlot] =
    useState<AvailabilitySlotListModel | null>(null);
  const navigate = useNavigate();

  const {
    fields: staffMemberFields,
    append: appendStaffMember,
    update: updateStaffMember,
    replace: replaceStaffMembers,
  } = useFieldArray({
    control: control,
    name: 'staffMemberAvailabilities',
  });

  // add staff members on initial load
  useEffect(() => {
    if (accountStaffMembers?.length) {
      replaceStaffMembers([]);

      const staffMembers = sortStaffMembers(
        accountStaffMembers.filter(x =>
          x.accountSiteStaffMembers.some(y => y.accountId === currentAccount?.id),
        ),
      );

      for (const staffMember of staffMembers) {
        appendStaffMember({ staffMember } as StaffMemberAvailabilityModel);
      }
    }
  }, [accountStaffMembers, currentAccount?.id]);

  const toggleDay = useCallback(
    (value: string) => {
      let currentValues = getValues('recurringOptions.daysOfWeek') as string[];
      if (!currentValues) {
        currentValues = [];
      }

      if (currentValues.includes(value)) {
        currentValues.splice(currentValues.indexOf(value), 1);
      } else {
        currentValues.push(value);
      }

      setValue('recurringOptions.daysOfWeek', currentValues);
    },
    [getValues, setValue],
  );

  useEffect(() => {
    const { unsubscribe } = watch((value, { name }) => {
      if (name === 'date' && value?.date) {
        const currentRecurringOptions = getValues('recurringOptions');

        // Only clear if there are values to clear
        if (currentRecurringOptions?.daysOfWeek?.length > 0) {
          setValue('recurringOptions.daysOfWeek', []);
        }

        if (currentRecurringOptions?.dayOfWeek !== 'Day') {
          setValue('recurringOptions.dayOfWeek', 'Day');
        }

        const date = DateTime.fromISO(value.date);
        if (date.isValid) {
          const newDayValue = daysOfWeek[date.weekday === 7 ? 0 : date.weekday].value;
          const newDayOfWeek = date.weekdayLong;

          // Use setTimeout to break the synchronous update cycle
          setTimeout(() => {
            toggleDay(newDayValue);
            setValue('recurringOptions.dayOfWeek', newDayOfWeek);
          }, 0);
        }
      }
    });
    return () => unsubscribe();
  }, [watch, getValues, setValue, toggleDay]);

  const onSubmit = async () => {
    // If not recurring, submit directly
    if (!isRecurring) {
      await confirmSubmit();
      return;
    }

    // If recurring, open confirmation dialog
    setConfirmRecurringOpen(true);
  };

  const confirmSubmit = async () => {
    setConfirmRecurringOpen(false);

    var value = form.getValues();

    const start = new Date(
      `${getDateFormat(value.date)} ${getTimeFormat(value.startTime)}`,
    ).toISOString();
    const end = new Date(
      `${getDateFormat(value.date)} ${getTimeFormat(value.endTime)}`,
    ).toISOString();

    const staffMemberAvailabilities = value.staffMemberAvailabilities.reduce<{
      [staffMemberId: number]: ConfirmationStatusEnum;
    }>((map, staffMemberAvailability) => {
      map[staffMemberAvailability.staffMember.id] =
        staffMemberAvailability.confirmationStatus?.confirmationStatusId;
      return map;
    }, {});

    const model: AvailabilitySlotPostModel = {
      accountId: currentAccount.id,
      start,
      end,
      officeNotes: value.officeNotes,
      accountMeetingLocationId: value.accountMeetingLocation.id,
      accountRepresentativeIds: value.accountRepresentativeIds,
      accountCategoryIds: value.accountCategoryIds,
      staffMemberAvailabilities: staffMemberAvailabilities,
      recurringOptions: getRecurringOptions(value),
    };

    try {
      await postAvailability(model).unwrap();
      toast.success(`Availability ${isRecurring ? 'slots' : 'slot'} saved!`);
      navigate('/calendar');
    } catch (error) {
      if (error.status === 409) {
        setConflictingAvailabilitySlot(error.data.conflictingAvailabilitySlot);
      } else {
        toast.error(
          `Error saving availability ${isRecurring ? 'slots' : 'slot'}. Please try again.`,
        );
      }
    }
  };

  const setConfirmationStatus = (
    value: FieldArrayWithId<NewAvailabilitySlotForm, 'staffMemberAvailabilities', 'id'>,
    status: ConfirmationStatusEnum,
    index: number,
  ) => {
    if (value.confirmationStatus?.confirmationStatusId === status) {
      return;
    }

    value.confirmationStatus = {
      confirmationStatusId: status,
      description: '',
    };
    updateStaffMember(index, value);
  };

  const handleRepsChange = (
    accountRepresentatives: AccountRepresentativeListModel[],
    accountCategories: AccountCategoryModel[],
  ) => {
    form.setValue(
      'accountRepresentativeIds',
      accountRepresentatives.map(x => x.id),
    );
    form.setValue(
      'accountCategoryIds',
      accountCategories.map(x => x.id),
    );
  };

  return (
    <>
      <NoAccountBanner />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col gap-6'>
            <Button
              color='default'
              variant='light'
              onPress={() => navigate(-1)}
              className='self-start'
            >
              <ArrowLeftIcon className='size-5' />
              Back
            </Button>

            <Heading icon={<CalendarIcon className='size-7' />}>Add New Availability</Heading>

            <div className='grid grid-cols-1 lg:grid-cols-5 gap-6'>
              <div className='lg:col-span-2 flex flex-col gap-6'>
                <Card shadow='sm' className='border border-gray-200'>
                  <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                    <ClockIcon className='size-6 text-primary-600' />
                    <Subheading>Basic Details</Subheading>
                  </CardHeader>
                  <Divider />
                  <CardBody className='p-6'>
                    <div className='flex flex-col gap-4'>
                      <Controller
                        control={control}
                        name='date'
                        rules={{ required: 'This field is required' }}
                        render={({ field, fieldState }) => (
                          <HeroInput
                            {...field}
                            type='date'
                            label='Date'
                            variant='bordered'
                            isRequired
                            isInvalid={fieldState.invalid}
                            errorMessage={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name='startTime'
                        rules={{ required: 'This field is required' }}
                        render={({ field, fieldState }) => (
                          <HeroInput
                            {...field}
                            type='time'
                            label='Start Time'
                            variant='bordered'
                            isRequired
                            isInvalid={fieldState.invalid}
                            errorMessage={fieldState.error?.message}
                            startContent={<ClockIcon className='size-4 text-default-400' />}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name='endTime'
                        rules={{ required: 'This field is required' }}
                        render={({ field, fieldState }) => (
                          <HeroInput
                            {...field}
                            type='time'
                            label='End Time'
                            variant='bordered'
                            isRequired
                            isInvalid={fieldState.invalid}
                            errorMessage={fieldState.error?.message}
                            startContent={<ClockIcon className='size-4 text-default-400' />}
                          />
                        )}
                      />
                      <MeetingLocationSelector name={'accountMeetingLocation'} required />
                      <Controller
                        control={control}
                        name='officeNotes'
                        render={({ field, fieldState }) => (
                          <HeroTextarea
                            {...field}
                            label='Instructions for Reps'
                            placeholder='Provide instructions for the representative (parking, building access, special requirements, etc.)'
                            variant='bordered'
                            isInvalid={fieldState.invalid}
                            errorMessage={fieldState.error?.message}
                            minRows={3}
                          />
                        )}
                      />
                    </div>
                  </CardBody>
                </Card>

                <Card shadow='sm' className='border border-gray-200'>
                  <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                    <ArrowPathIcon className='size-6 text-primary-600' />
                    <Subheading>Recurring Options</Subheading>
                  </CardHeader>
                  <Divider />
                  <CardBody className='p-6'>
                    <Fieldset className='pb-5'>
                      <SwitchField>
                        <Label>Recurring</Label>
                        <Switch color='blue' checked={isRecurring} onChange={setIsRecurring} />
                      </SwitchField>
                    </Fieldset>
                    {isRecurring && (
                      <>
                        <Divider className='my-4' />
                        <div className='flex flex-col gap-6 w-full self-center max-w-screen-md'>
                          <Controller
                            control={control}
                            name='recurringOptions.frequency'
                            render={({ field }) => (
                              <HeroSelect
                                {...field}
                                label='Recur'
                                variant='bordered'
                                selectedKeys={
                                  field.value ? new Set([field.value.toString()]) : new Set([])
                                }
                                onSelectionChange={keys => {
                                  const selectedValue = Array.from(keys)[0];
                                  field.onChange(
                                    selectedValue ? parseInt(selectedValue as string) : null,
                                  );
                                }}
                                isRequired
                              >
                                <SelectItem key={RecurringFrequencyEnum.Day.toString()}>
                                  Daily
                                </SelectItem>
                                <SelectItem key={RecurringFrequencyEnum.Week.toString()}>
                                  Weekly
                                </SelectItem>
                                <SelectItem key={RecurringFrequencyEnum.Month.toString()}>
                                  Monthly
                                </SelectItem>
                              </HeroSelect>
                            )}
                          />

                          {recurringFrequencyWatch == RecurringFrequencyEnum.Day && (
                            <div className='flex flex-col gap-6'>
                              <SwitchField>
                                <Label>Every Weekday</Label>
                                <Controller
                                  control={control}
                                  name='recurringOptions.everyWeekday'
                                  render={({ field: { onChange, onBlur, value } }) => (
                                    <Switch
                                      color='blue'
                                      onChange={onChange}
                                      onBlur={onBlur}
                                      checked={value}
                                    />
                                  )}
                                />
                              </SwitchField>
                              <div className='flex flex-row items-center gap-3'>
                                <span className='text-sm text-default-600'>Or Every</span>
                                <Controller
                                  control={control}
                                  name='recurringOptions.recurEvery'
                                  render={({ field }) => (
                                    <HeroInput
                                      {...field}
                                      value={field.value?.toString() || ''}
                                      onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                                      type='number'
                                      variant='bordered'
                                      size='sm'
                                      className='max-w-20'
                                      isDisabled={watch('recurringOptions.everyWeekday')}
                                    />
                                  )}
                                />
                                <span className='text-sm text-default-600'>day(s)</span>
                              </div>
                            </div>
                          )}

                          {recurringFrequencyWatch == RecurringFrequencyEnum.Week && (
                            <div className='flex flex-col gap-4'>
                              <div className='flex flex-row items-center gap-3'>
                                <span className='text-sm text-default-600'>Every</span>
                                <Controller
                                  control={control}
                                  name='recurringOptions.recurEvery'
                                  render={({ field }) => (
                                    <HeroInput
                                      {...field}
                                      value={field.value?.toString() || ''}
                                      onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                                      type='number'
                                      variant='bordered'
                                      size='sm'
                                      className='max-w-20'
                                    />
                                  )}
                                />
                                <span className='text-sm text-default-600'>week(s) on:</span>
                              </div>
                              <div className='flex flex-col gap-2'>
                                <span className='text-sm text-default-600'>Create On</span>
                                <div className='flex items-start gap-2'>
                                  {daysOfWeek.map(val => (
                                    <Chip
                                      key={val.value}
                                      className='cursor-pointer'
                                      color={
                                        daysOfWeekWatch?.includes(val.value) ? 'primary' : 'default'
                                      }
                                      variant={
                                        daysOfWeekWatch?.includes(val.value) ? 'solid' : 'bordered'
                                      }
                                      onClick={() => toggleDay(val.value)}
                                      size='md'
                                    >
                                      {val.label}
                                    </Chip>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          {recurringFrequencyWatch == RecurringFrequencyEnum.Month && (
                            <div className='flex flex-col gap-6'>
                              <RadioGroup
                                value={monthlySelection}
                                onValueChange={e => setMonthlySelection(e as 'date' | 'week')}
                                orientation='horizontal'
                              >
                                <HeroRadio value='date'>Date</HeroRadio>
                                <HeroRadio value='week'>Week</HeroRadio>
                              </RadioGroup>

                              {monthlySelection === 'date' && (
                                <div className='flex items-center gap-3'>
                                  <span className='text-sm text-default-600'>on day</span>
                                  <Controller
                                    control={control}
                                    name='recurringOptions.dateOfMonth'
                                    render={({ field }) => (
                                      <HeroInput
                                        {...field}
                                        value={field.value?.toString() || ''}
                                        onChange={e =>
                                          field.onChange(parseInt(e.target.value) || 0)
                                        }
                                        type='number'
                                        variant='bordered'
                                        size='sm'
                                        className='max-w-12'
                                      />
                                    )}
                                  />
                                  <span className='text-sm text-default-600'>of every</span>
                                  <Controller
                                    control={control}
                                    name='recurringOptions.recurEvery'
                                    render={({ field }) => (
                                      <HeroInput
                                        {...field}
                                        value={field.value?.toString() || ''}
                                        onChange={e =>
                                          field.onChange(parseInt(e.target.value) || 0)
                                        }
                                        type='number'
                                        variant='bordered'
                                        size='sm'
                                        className='max-w-12'
                                      />
                                    )}
                                  />
                                  <span className='text-sm text-default-600'>month(s)</span>
                                </div>
                              )}

                              {monthlySelection === 'week' && (
                                <div className='flex items-center gap-3 flex-wrap'>
                                  <span className='text-sm text-default-600'>on the</span>
                                  <Controller
                                    control={control}
                                    name='recurringOptions.weekOfMonth'
                                    render={({ field }) => (
                                      <HeroSelect
                                        {...field}
                                        variant='bordered'
                                        size='sm'
                                        className='min-w-24'
                                        selectedKeys={
                                          field.value
                                            ? new Set([field.value.toString()])
                                            : new Set([])
                                        }
                                        onSelectionChange={keys => {
                                          const selectedValue = Array.from(keys)[0];
                                          field.onChange(
                                            selectedValue
                                              ? parseInt(selectedValue as string)
                                              : null,
                                          );
                                        }}
                                      >
                                        <SelectItem key={WeekOfMonthEnum.First.toString()}>
                                          First
                                        </SelectItem>
                                        <SelectItem key={WeekOfMonthEnum.Second.toString()}>
                                          Second
                                        </SelectItem>
                                        <SelectItem key={WeekOfMonthEnum.Third.toString()}>
                                          Third
                                        </SelectItem>
                                        <SelectItem key={WeekOfMonthEnum.Fourth.toString()}>
                                          Fourth
                                        </SelectItem>
                                        <SelectItem key={WeekOfMonthEnum.Last.toString()}>
                                          Last
                                        </SelectItem>
                                      </HeroSelect>
                                    )}
                                  />
                                  <Controller
                                    control={control}
                                    name='recurringOptions.dayOfWeek'
                                    render={({ field }) => (
                                      <HeroSelect
                                        {...field}
                                        variant='bordered'
                                        size='sm'
                                        className='min-w-36'
                                        selectedKeys={
                                          field.value ? new Set([field.value]) : new Set([])
                                        }
                                        onSelectionChange={keys => {
                                          const selectedValue = Array.from(keys)[0];
                                          field.onChange(selectedValue || null);
                                        }}
                                      >
                                        {daysOfWeekListForMonth?.length > 0 &&
                                          daysOfWeekListForMonth.map((option: string) => (
                                            <SelectItem key={option}>{option}</SelectItem>
                                          ))}
                                      </HeroSelect>
                                    )}
                                  />
                                  <span className='text-sm text-default-600'>of every</span>
                                  <Controller
                                    control={control}
                                    name='recurringOptions.recurEvery'
                                    render={({ field }) => (
                                      <HeroInput
                                        {...field}
                                        value={field.value?.toString() || ''}
                                        onChange={e =>
                                          field.onChange(parseInt(e.target.value) || 0)
                                        }
                                        type='number'
                                        variant='bordered'
                                        size='sm'
                                        className='max-w-24'
                                      />
                                    )}
                                  />
                                  <span className='text-sm text-default-600'>month(s)</span>
                                </div>
                              )}
                            </div>
                          )}
                          <Controller
                            control={control}
                            name='recurringOptions.endDate'
                            rules={{ required: 'This field is required' }}
                            render={({ field, fieldState }) => (
                              <HeroInput
                                {...field}
                                type='date'
                                label='End by'
                                variant='bordered'
                                isRequired
                                isInvalid={fieldState.invalid}
                                errorMessage={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </div>

              <div className='lg:col-span-3 flex flex-col gap-6'>
                <Card shadow='sm' className='border border-gray-200'>
                  <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                    <CalendarIcon className='size-6 text-primary-600' />
                    <Subheading>Representative Access</Subheading>
                  </CardHeader>
                  <Divider />
                  <CardBody className='p-6'>
                    <RepresentativeAccess onChange={handleRepsChange} />
                  </CardBody>
                </Card>

                <Card shadow='sm' className='border border-gray-200'>
                  <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                    <UserGroupIcon className='size-6 text-primary-600' />
                    <Subheading>Staff Availability</Subheading>
                  </CardHeader>
                  <Divider />
                  <CardBody className='p-6'>
                    <StaffAvailabilityList
                      staffMemberAvailabilities={staffMemberFields}
                      accountId={currentAccount?.id}
                      isEditable={true}
                      onStatusChange={(_staffMemberId, status, index) => {
                        if (index !== undefined) {
                          const staff = staffMemberFields[index];
                          setConfirmationStatus(staff, status, index);
                        }
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            </div>

            <div className='flex pt-8'>
              <Button
                isDisabled={!currentAccount}
                color='primary'
                type='submit'
                isLoading={isLoading || postLoading}
                size='lg'
              >
                Create Availability {isRecurring ? 'Slots' : 'Slot'}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      <Dialog open={confirmRecurringOpen} onClose={() => setConfirmRecurringOpen(false)}>
        <DialogTitle>Confirm Recurring Availability Slots</DialogTitle>
        <DialogBody>
          <p>You are about to create recurring availability slots with the following details:</p>

          <dl className='mt-4'>
            <dt className='font-bold inline-block mr-2'>Time:</dt>
            <dd className='inline-block'>
              {formatDate(form.getValues().startTime, DateTime.TIME_SIMPLE)} -{' '}
              {formatDate(form.getValues().endTime, DateTime.TIME_SIMPLE)}
            </dd>
            <br />

            <dt className='font-bold inline-block mr-2'>Start Date:</dt>
            <dd className='inline-block'>
              {formatDate(form.getValues().date, DateTime.DATE_FULL)}
            </dd>
            <br />

            <dt className='font-bold inline-block mr-2'>End Date:</dt>
            <dd className='inline-block'>
              {formatDate(form.getValues().recurringOptions.endDate, DateTime.DATE_FULL)}
            </dd>
            <br />

            <dt className='font-bold inline-block mr-2'>Recurrence:</dt>
            <dd className='inline-block'>{recurrenceDescription()}</dd>
          </dl>
        </DialogBody>
        <DialogActions>
          <Button isLoading={postLoading} color='primary' variant='solid' onPress={confirmSubmit}>
            Confirm
          </Button>
          <Button color='default' variant='bordered' onPress={() => setConfirmRecurringOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!conflictingAvailabilitySlot}
        onClose={() => setConflictingAvailabilitySlot(null)}
      >
        <DialogTitle>Conflict</DialogTitle>
        <DialogBody>
          <p>
            This availability slot cannot be created because it conflicts with an existing
            availability slot:
          </p>

          {conflictingAvailabilitySlot && (
            <div className='mt-4'>
              <EventTimeAndLocation
                start={conflictingAvailabilitySlot.start}
                end={conflictingAvailabilitySlot.end}
                location={conflictingAvailabilitySlot.accountMeetingLocation.description}
              />
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button
            color='default'
            variant='bordered'
            onPress={() => setConflictingAvailabilitySlot(null)}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  function getRecurringOptions(value: NewAvailabilitySlotForm): RecurringOptions {
    if (!isRecurring || !value.recurringOptions || !value.recurringOptions.frequency) {
      return null;
    }

    switch (+value.recurringOptions.frequency) {
      case RecurringFrequencyEnum.Day:
        return {
          timeZone: userTimeZone,
          frequency: +value.recurringOptions.frequency,
          endDate: value.recurringOptions.endDate,
          everyWeekday: value.recurringOptions.everyWeekday,
          recurEvery: value.recurringOptions.everyWeekday
            ? undefined
            : value.recurringOptions.recurEvery,
        };
      case RecurringFrequencyEnum.Week:
        return {
          timeZone: userTimeZone,
          frequency: +value.recurringOptions.frequency,
          recurEvery: value.recurringOptions.recurEvery,
          endDate: value.recurringOptions.endDate,
          daysOfWeek: value.recurringOptions.daysOfWeek,
        };
      case RecurringFrequencyEnum.Month:
        return {
          timeZone: userTimeZone,
          frequency: +value.recurringOptions.frequency,
          recurEvery: value.recurringOptions.recurEvery,
          endDate: value.recurringOptions.endDate,
          dateOfMonth: monthlySelection === 'date' ? value.recurringOptions.dateOfMonth : undefined,
          weekOfMonth:
            monthlySelection === 'week' ? +value.recurringOptions.weekOfMonth : undefined,
          dayOfWeek: monthlySelection === 'week' ? value.recurringOptions.dayOfWeek : undefined,
        };
      default:
        throw new Error(`Unknown recurring frequency: ${value.recurringOptions.frequency}`);
    }
  }

  function recurrenceDescription(): string {
    var recurringOptions = getRecurringOptions(form.getValues());

    if (!recurringOptions) {
      return null;
    }

    switch (recurringOptions.frequency) {
      case RecurringFrequencyEnum.Day: {
        if (recurringOptions.everyWeekday === true) {
          return 'every weekday';
        }

        return recurringOptions.recurEvery === 1
          ? 'daily'
          : `every ${recurringOptions.recurEvery} days`;
      }

      case RecurringFrequencyEnum.Week: {
        if (recurringOptions.daysOfWeek && recurringOptions.daysOfWeek.length !== 0) {
          const days = recurringOptions.daysOfWeek.join(', ');
          return recurringOptions.recurEvery === 1
            ? `weekly on ${days}`
            : `every ${recurringOptions.recurEvery} weeks on ${days}`;
        }

        return recurringOptions.recurEvery === 1
          ? 'weekly'
          : `every ${recurringOptions.recurEvery} weeks`;
      }

      case RecurringFrequencyEnum.Month: {
        if (recurringOptions.dateOfMonth != null) {
          return recurringOptions.recurEvery === 1
            ? `monthly on the ${ordinal(recurringOptions.dateOfMonth)}`
            : `every ${recurringOptions.recurEvery} months on the ${ordinal(recurringOptions.dateOfMonth)}`;
        } else if (recurringOptions.weekOfMonth != null && recurringOptions.dayOfWeek) {
          const iteration =
            recurringOptions.weekOfMonth === WeekOfMonthEnum.First
              ? 'first'
              : recurringOptions.weekOfMonth === WeekOfMonthEnum.Second
                ? 'second'
                : recurringOptions.weekOfMonth === WeekOfMonthEnum.Third
                  ? 'third'
                  : recurringOptions.weekOfMonth === WeekOfMonthEnum.Fourth
                    ? 'fourth'
                    : recurringOptions.weekOfMonth === WeekOfMonthEnum.Last
                      ? 'last'
                      : '';

          return recurringOptions.recurEvery === 1
            ? `monthly on the ${iteration} ${recurringOptions.dayOfWeek}`
            : `every ${recurringOptions.recurEvery} months on the ${iteration} ${recurringOptions.dayOfWeek}`;
        }

        return recurringOptions.recurEvery === 1
          ? 'monthly'
          : `every ${recurringOptions.recurEvery} months`;
      }

      default:
        throw new Error(`Unknown recurring frequency: ${recurringOptions.frequency}`);
    }
  }
};

export default NewAvailability;

function ordinal(n: number): string {
  if (n <= 0) {
    return n.toString();
  }

  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }

  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
